import { z } from 'zod';
import type { MoneyJson } from '../money/money';
import { moneySchema } from './common.schemas';
import type { CostDecisionsView, WarrantyClaimView, WorkOrderFlagsView } from './work-order-flags.schemas';
import type { WorkOrderVerificationView } from './work-order-verification.schemas';

/**
 * What a work order records (gitaumoses4/chukua#170): details, labour, parts, downtime and evidence. All of it is
 * append-only: a correction is a new row naming the one it replaces, and `{ void: true }` retracts with no replacement.
 */

/** Hard-coded in a CHECK on `work_order_details`, so a new workshop is a migration. */
export const WORKSHOPS = ['in_house', 'garage'] as const;
export type Workshop = (typeof WORKSHOPS)[number];

/** Hard-coded in a CHECK on `work_order_evidence`, so a new kind is a migration. */
export const WORK_ORDER_EVIDENCE_KINDS = ['photo_before', 'photo_after', 'garage_invoice'] as const;
export type WorkOrderEvidenceKind = (typeof WORK_ORDER_EVIDENCE_KINDS)[number];

/** The entity type a piece of evidence is uploaded under, with the work order's id as the entity id. */
export const WORK_ORDER_EVIDENCE_UPLOAD_ENTITY_TYPE = 'work_order_evidence';

const text = (max: number) => z.string().trim().min(1).max(max);
const nonNegativeMoney = moneySchema.refine((money) => money.amountMinor >= 0, { message: 'An amount cannot be negative', path: ['amountMinor'] });
const reasonSchema = text(500);

/** `POST /work-orders/:id/details` — a new revision; the newest one is what the order shows. */
export const recordWorkOrderDetailsInputSchema = z
  .object({
    odometerKm: z.number().int().min(0).max(10_000_000).optional(),
    symptoms: text(2000).optional(),
    diagnosis: text(4000).optional(),
    workshop: z.enum(WORKSHOPS),
    garageId: z.uuid().optional(),
    /** Whole months, on external work only. */
    warrantyMonths: z.number().int().min(1).max(120).optional(),
  })
  .strict()
  .refine((input) => (input.workshop === 'garage') === (input.garageId !== undefined), {
    message: 'A garage is named when the workshop is a garage, and only then',
    path: ['garageId'],
  })
  .refine((input) => input.warrantyMonths === undefined || input.workshop === 'garage', {
    message: 'A warranty period is for external work',
    path: ['warrantyMonths'],
  });
export type RecordWorkOrderDetailsInput = z.infer<typeof recordWorkOrderDetailsInputSchema>;

export const recordLabourInputSchema = z
  .object({
    description: text(500),
    amount: nonNegativeMoney,
    hours: z.number().positive().max(9999.99).multipleOf(0.01).optional(),
    mechanic: text(200).optional(),
  })
  .strict();
export type RecordLabourInput = z.infer<typeof recordLabourInputSchema>;

export const recordPartsLineInputSchema = z
  .object({
    part: text(200),
    /** The register groups by type (gitaumoses4/chukua#174); older lines have none, a new or corrected one names it. */
    partTypeId: z.uuid(),
    partNumber: text(100).optional(),
    /** The unit's own serial, where it has one; what a warranty claim quotes. */
    serial: text(100).optional(),
    /** A whole number, so a line's total is exact; litres and the like are priced per unit. */
    quantity: z.number().int().min(1).max(100_000),
    unitCost: nonNegativeMoney,
    supplierId: z.uuid().optional(),
  })
  .strict();
export type RecordPartsLineInput = z.infer<typeof recordPartsLineInputSchema>;

const isoInstant = z.iso.datetime({ offset: true });

export const recordDowntimeInputSchema = z
  .object({ startedAt: isoInstant, endedAt: isoInstant, reason: text(500).optional() })
  .strict()
  .refine((input) => new Date(input.endedAt) >= new Date(input.startedAt), { message: 'Downtime cannot end before it starts', path: ['endedAt'] });
export type RecordDowntimeInput = z.infer<typeof recordDowntimeInputSchema>;

export const recordEvidenceInputSchema = z.object({ kind: z.enum(WORK_ORDER_EVIDENCE_KINDS), uploadId: z.uuid(), note: text(1000).optional() }).strict();
export type RecordEvidenceInput = z.infer<typeof recordEvidenceInputSchema>;

/** A correction either replaces the row (the whole new value) or retracts it. Both carry why. */
export const voidRecordInputSchema = z.object({ void: z.literal(true), reason: reasonSchema }).strict();
export type VoidRecordInput = z.infer<typeof voidRecordInputSchema>;

function correctionSchema<T extends z.ZodType>(replacement: T) {
  return z.union([voidRecordInputSchema, z.object({ replacement, reason: reasonSchema }).strict()]);
}
export const correctLabourInputSchema = correctionSchema(recordLabourInputSchema);
export const correctPartsLineInputSchema = correctionSchema(recordPartsLineInputSchema);
export const correctDowntimeInputSchema = correctionSchema(recordDowntimeInputSchema);
export const correctEvidenceInputSchema = correctionSchema(recordEvidenceInputSchema);
export type CorrectLabourInput = z.infer<typeof correctLabourInputSchema>;
export type CorrectPartsLineInput = z.infer<typeof correctPartsLineInputSchema>;
export type CorrectDowntimeInput = z.infer<typeof correctDowntimeInputSchema>;
export type CorrectEvidenceInput = z.infer<typeof correctEvidenceInputSchema>;

/** Fields every recorded row carries. */
interface RecordedRowView {
  id: string;
  workOrderId: string;
  recordedById: string;
  createdAt: string;
  /** The row this one replaces or retracts. */
  correctsId: string | null;
  isVoid: boolean;
}

export interface WorkOrderDetailsView {
  id: string;
  workOrderId: string;
  recordedById: string;
  createdAt: string;
  correctsId: string | null;
  odometerKm: number | null;
  symptoms: string | null;
  diagnosis: string | null;
  workshop: Workshop;
  garageId: string | null;
  garageName: string | null;
  warrantyMonths: number | null;
}

export interface WorkOrderLabourView extends RecordedRowView {
  description: string;
  amount: MoneyJson;
  hours: number | null;
  mechanic: string | null;
}

export interface WorkOrderPartsLineView extends RecordedRowView {
  part: string;
  /** Null on a line recorded before part types existed. */
  partTypeId: string | null;
  partTypeName: string | null;
  partNumber: string | null;
  serial: string | null;
  quantity: number;
  unitCost: MoneyJson;
  /** `quantity × unitCost`, derived and never stored. */
  lineTotal: MoneyJson;
  supplierId: string | null;
  supplierName: string | null;
}

export interface WorkOrderDowntimeView extends RecordedRowView {
  startedAt: string;
  endedAt: string;
  durationMinutes: number;
  reason: string | null;
}

export interface WorkOrderEvidenceView extends RecordedRowView {
  kind: WorkOrderEvidenceKind;
  uploadId: string;
  note: string | null;
}

/** `GET /work-orders/:id/record` — the effective rows, their totals and the estimate they are compared with. */
export interface WorkOrderRecordView {
  details: WorkOrderDetailsView | null;
  labour: WorkOrderLabourView[];
  parts: WorkOrderPartsLineView[];
  downtime: WorkOrderDowntimeView[];
  evidence: WorkOrderEvidenceView[];
  /** Every verification attempt, oldest first (gitaumoses4/chukua#171). */
  verifications: WorkOrderVerificationView[];
  /** Derived on every read (gitaumoses4/chukua#175): the flags that count, and every trigger with its verdict. */
  flags: WorkOrderFlagsView;
  /** Claims raised on this order against the garage that did the earlier work, and who bears the cost. */
  claims: WarrantyClaimView[];
  costDecisions: CostDecisionsView;
  totals: { labour: MoneyJson; parts: MoneyJson; actual: MoneyJson; estimate: MoneyJson; downtimeMinutes: number };
}
